import { useEffect, useMemo } from "react";

import { type ChartDataset } from "chart.js";
import moment from "moment";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type DateInterval } from "@north-beam/nb-common";

import { CUSTOM_ANNOTATION_ID } from "../annotation-chart-plugin/annotation-plugin-helpers";

import { useAnnotationParam } from "./use-annotation-param";
import { useAnnotations } from "./use-annotations";

type AnnotationMetadata = {
  annotatedDates: string[];
  datesUsedForTraining?: string[];
  showAnnotations: (dateRange: DateInterval) => void;
  createAnnotation: (annotatedDate: string) => void;
  timezone: string;
  viewable?: any;
};

export type AnnotationDataset = ChartDataset<"line", { date: string }[]> & {
  metadata: AnnotationMetadata;
};

export const useBuildAnnotationDataset = (
  startDate: string,
  endDate: string,
  timezone: string,
): AnnotationDataset => {
  const {
    showCreateAnnotationOnDate,
    listDateRangeAnnotations,
    listFilteredDateRangeAnnotations,
  } = useAnnotationParam();
  const { annotations, fetchAnnotations } = useAnnotations(true);
  const prettyLogEventPage = usePrettyLogEventPage();

  useEffect(() => {
    fetchAnnotations({ variables: { startDate, endDate } });
  }, [startDate, endDate, fetchAnnotations]);

  const dates = useMemo(() => {
    const dates = [];
    const current = moment(startDate);

    while (current.isSameOrBefore(endDate)) {
      dates.push(moment(current).toISOString().split("T")[0]);
      current.add(1, "day");
    }

    return dates.map((date) => ({ date }));
  }, [endDate, startDate]);

  const metadata = useMemo(() => {
    const annotatedDates = annotations.map(
      ({ annotatedDate }) => annotatedDate,
    );
    const showAnnotations = (dateRange: DateInterval) => {
      listDateRangeAnnotations({ startDate, endDate });
      listFilteredDateRangeAnnotations(dateRange);

      prettyLogEventPage({
        domain: "Annotations",
        action: "onClick",
        additionalActionInfo: "show annotations graph icon",
        data: { dateRange },
      });
    };
    const createAnnotation = (annotatedDate: string) => {
      listDateRangeAnnotations({ startDate, endDate });
      showCreateAnnotationOnDate(annotatedDate);

      prettyLogEventPage({
        domain: "Annotations",
        action: "onClick",
        additionalActionInfo: "add annotations graph icon",
        data: { annotatedDate },
      });
    };

    return {
      annotatedDates,
      showAnnotations,
      createAnnotation,
      timezone,
    };
  }, [
    listDateRangeAnnotations,
    prettyLogEventPage,
    annotations,
    showCreateAnnotationOnDate,
    listFilteredDateRangeAnnotations,
    startDate,
    endDate,
    timezone,
  ]);

  return useMemo(() => {
    return {
      data: dates,
      hidden: true,
      parsing: { xAxisKey: "date" },
      label: CUSTOM_ANNOTATION_ID,
      metadata,
    };
  }, [dates, metadata]);
};
